import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";

type IconButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
	icon: LucideIcon;
	label: string;
};

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
	({ icon: Icon, label, ...props }, ref) => (
		<Button
			ref={ref}
			type="button"
			variant="ghost"
			size="icon-xs"
			aria-label={label}
			className="size-7 text-muted-foreground hover:bg-muted-subtle hover:text-foreground"
			{...props}
		>
			<Icon className="size-4" />
		</Button>
	),
);
IconButton.displayName = "IconButton";

export function ToolbarTooltip({
	label,
	children,
}: {
	label: string;
	children: ReactNode;
}) {
	return (
		<Tooltip>
			<TooltipTrigger asChild>{children}</TooltipTrigger>
			<TooltipContent>{label}</TooltipContent>
		</Tooltip>
	);
}

export function ToolbarButton(props: IconButtonProps & { tooltip?: string }) {
	const { tooltip, ...rest } = props;
	return (
		<ToolbarTooltip label={tooltip ?? rest.label}>
			<IconButton {...rest} />
		</ToolbarTooltip>
	);
}

export function ToolbarDivider() {
	return <span aria-hidden className="mx-1.5 h-5 w-px bg-border" />;
}
